import React, { useState } from 'react';
import {
    Card,
    Row,
    Col,
    Statistic,
    Table,
    Spin,
    Alert,
    DatePicker,
    Select,
    Space,
    Tag,
    Progress,
    Typography,
    Tooltip,
    Empty
} from 'antd';
import {
    ArrowUpOutlined,
    ArrowDownOutlined,
    UserOutlined,
    ClockCircleOutlined,
    GlobalOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    FireOutlined,
    LineChartOutlined,
    BarChartOutlined
} from '@ant-design/icons';
import { useTheme } from '@/components/common/ThemeProvider';
import { useGetLoginAnalytics } from '../hooks/useUserSecurity';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

// Types for analytics data
interface DailyLoginStats {
    date: string;
    totalAttempts: number;
    successfulLogins: number;
    failedLogins: number;
    uniqueUsers: number;
}

interface HourlyLoginStats {
    hour: number;
    hourLabel: string;
    totalAttempts: number;
    successfulLogins: number;
    failedLogins: number;
}

interface UserLoginStats {
    username: string;
    totalAttempts: number;
    successfulLogins: number;
    failedLogins: number;
    lastAttemptTime: string;
}

interface IpLoginStats {
    ipAddress: string;
    totalAttempts: number;
    successfulLogins: number;
    failedLogins: number;
    uniqueUsers: number;
}

interface LoginAnalyticsData {
    totalAttempts: number;
    successfulLogins: number;
    failedLogins: number;
    uniqueUsers: number;
    uniqueIpAddresses: number;
    successRate: number;
    dailyStats: DailyLoginStats[];
    hourlyDistribution: HourlyLoginStats[];
    topActiveUsers: UserLoginStats[];
    topFailedUsers: UserLoginStats[];
    topIpAddresses: IpLoginStats[];
    previousPeriodAttempts: number;
    changePercentage: number | null;
    peakDay: string;
    peakHour: string;
    peakDayCount: number;
    peakHourCount: number;
    startDate: string;
    endDate: string;
    periodDays: number;
}

export const LoginAnalytics: React.FC = () => {
    const { dark } = useTheme();
    const [days, setDays] = useState(30);
    const [dateRange, setDateRange] = useState<[string, string] | null>(null);

    const { data: analytics, isLoading, error } = useGetLoginAnalytics(
        days,
        dateRange?.[0],
        dateRange?.[1]
    ) as { data: LoginAnalyticsData | undefined; isLoading: boolean; error: Error | null };

    const handleDateRangeChange = (dates: any, dateStrings: [string, string]) => {
        if (dates) {
            setDateRange(dateStrings);
        } else {
            setDateRange(null);
        }
    };

    const handlePeriodChange = (value: number) => {
        setDays(value);
        setDateRange(null);
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-60">
                <Spin size="large" tip="Loading analytics..." />
            </div>
        );
    }

    if (error) {
        return (
            <Alert
                message="Error Loading Analytics"
                description="Failed to load login analytics data. Please try again later."
                type="error"
                showIcon
            />
        );
    }

    if (!analytics) {
        return <Empty description="No analytics data available" />;
    }

    // Calculate max values for charts
    const maxDailyAttempts = Math.max(...(analytics.dailyStats?.map(d => d.totalAttempts) || [1]));
    const maxHourlyAttempts = Math.max(...(analytics.hourlyDistribution?.map(h => h.totalAttempts) || [1]));

    // Top users columns
    const topUsersColumns = [
        {
            title: 'Username',
            dataIndex: 'username',
            key: 'username',
            render: (text: string) => (
                <Space>
                    <UserOutlined />
                    <strong>{text}</strong>
                </Space>
            ),
        },
        {
            title: 'Total',
            dataIndex: 'totalAttempts',
            key: 'totalAttempts',
            sorter: (a: UserLoginStats, b: UserLoginStats) => a.totalAttempts - b.totalAttempts,
        },
        {
            title: 'Success',
            dataIndex: 'successfulLogins',
            key: 'successfulLogins',
            render: (val: number) => <Tag color="success">{val}</Tag>,
        },
        {
            title: 'Failed',
            dataIndex: 'failedLogins',
            key: 'failedLogins',
            render: (val: number) => <Tag color={val > 0 ? 'error' : 'default'}>{val}</Tag>,
        },
        {
            title: 'Last Attempt',
            dataIndex: 'lastAttemptTime',
            key: 'lastAttemptTime',
            render: (text: string) => text ? dayjs(text).format('DD/MM HH:mm') : '-',
        },
    ];

    // Top IPs columns
    const topIpsColumns = [
        {
            title: 'IP Address',
            dataIndex: 'ipAddress',
            key: 'ipAddress',
            render: (text: string) => (
                <Space>
                    <GlobalOutlined />
                    <code>{text}</code>
                </Space>
            ),
        },
        {
            title: 'Total',
            dataIndex: 'totalAttempts',
            key: 'totalAttempts',
            sorter: (a: IpLoginStats, b: IpLoginStats) => a.totalAttempts - b.totalAttempts,
        },
        {
            title: 'Success',
            dataIndex: 'successfulLogins',
            key: 'successfulLogins',
            render: (val: number) => <Tag color="success">{val}</Tag>,
        },
        {
            title: 'Failed',
            dataIndex: 'failedLogins',
            key: 'failedLogins',
            render: (val: number) => <Tag color={val > 0 ? 'error' : 'default'}>{val}</Tag>,
        },
        {
            title: 'Unique Users',
            dataIndex: 'uniqueUsers',
            key: 'uniqueUsers',
        },
    ];

    return (
        <div className="space-y-6">
            {/* Filters */}
            <Card size="small">
                <Space wrap>
                    <Text strong>Period:</Text>
                    <Select
                        value={dateRange ? 0 : days}
                        onChange={handlePeriodChange}
                        style={{ width: 150 }}
                        options={[
                            { value: 7, label: 'Last 7 days' },
                            { value: 14, label: 'Last 14 days' },
                            { value: 30, label: 'Last 30 days' },
                            { value: 60, label: 'Last 60 days' },
                            { value: 90, label: 'Last 90 days' },
                        ]}
                    />
                    <Text strong>Or custom range:</Text>
                    <RangePicker
                        onChange={handleDateRangeChange}
                        value={dateRange ? [dayjs(dateRange[0]), dayjs(dateRange[1])] : null}
                    />
                </Space>
            </Card>

            {/* Summary Statistics */}
            <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title="Total Login Attempts"
                            value={analytics.totalAttempts}
                            prefix={<LineChartOutlined />}
                            suffix={
                                analytics.changePercentage !== null && (
                                    <span className={`text-sm ${analytics.changePercentage >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                                        {analytics.changePercentage >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                                        {Math.abs(analytics.changePercentage)}%
                                    </span>
                                )
                            }
                        />
                        <Text type="secondary" className="text-xs">
                            vs previous {analytics.periodDays} days ({analytics.previousPeriodAttempts})
                        </Text>
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title="Successful Logins"
                            value={analytics.successfulLogins}
                            prefix={<CheckCircleOutlined />}
                            valueStyle={{ color: '#3f8600' }}
                        />
                        <Progress
                            percent={analytics.successRate}
                            size="small"
                            status="success"
                            format={() => `${analytics.successRate}% success rate`}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title="Failed Logins"
                            value={analytics.failedLogins}
                            prefix={<CloseCircleOutlined />}
                            valueStyle={{ color: '#cf1322' }}
                        />
                        <Progress
                            percent={100 - analytics.successRate}
                            size="small"
                            status="exception"
                            format={() => `${(100 - analytics.successRate).toFixed(1)}% failure rate`}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title="Unique Users"
                            value={analytics.uniqueUsers}
                            prefix={<UserOutlined />}
                        />
                        <Text type="secondary" className="text-xs">
                            From {analytics.uniqueIpAddresses} unique IPs
                        </Text>
                    </Card>
                </Col>
            </Row>

            {/* Peak Usage Alert */}
            {analytics.peakDay && (
                <Alert
                    message={
                        <Space>
                            <FireOutlined />
                            <span>
                                <strong>Peak Activity:</strong> {analytics.peakDayCount} logins on {dayjs(analytics.peakDay).format('MMMM D, YYYY')}
                                {analytics.peakHour && ` | Peak hour: ${analytics.peakHour} (${analytics.peakHourCount} attempts)`}
                            </span>
                        </Space>
                    }
                    type="info"
                    showIcon={false}
                />
            )}

            {/* Daily Trend Chart (using Progress bars as simple visualization) */}
            <Card
                title={
                    <Space>
                        <BarChartOutlined />
                        <span>Daily Login Trend</span>
                    </Space>
                }
            >
                {analytics.dailyStats && analytics.dailyStats.length > 0 ? (
                    <div className="space-y-2">
                        {analytics.dailyStats.map((day) => (
                            <div key={day.date} className="flex items-center gap-4">
                                <div className="w-24 text-sm text-gray-500">
                                    {dayjs(day.date).format('MMM DD')}
                                </div>
                                <div className="flex-1">
                                    <Tooltip
                                        title={`Total: ${day.totalAttempts} | Success: ${day.successfulLogins} | Failed: ${day.failedLogins} | Users: ${day.uniqueUsers}`}
                                    >
                                        <div className="flex h-6 rounded overflow-hidden">
                                            <div
                                                className="bg-green-500 transition-all"
                                                style={{ width: `${(day.successfulLogins / maxDailyAttempts) * 100}%` }}
                                            />
                                            <div
                                                className="bg-red-500 transition-all"
                                                style={{ width: `${(day.failedLogins / maxDailyAttempts) * 100}%` }}
                                            />
                                        </div>
                                    </Tooltip>
                                </div>
                                <div className="w-16 text-right text-sm font-medium">
                                    {day.totalAttempts}
                                </div>
                            </div>
                        ))}
                        <div className="flex items-center gap-4 mt-4 text-sm">
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-4 bg-green-500 rounded" />
                                <span>Successful</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-4 bg-red-500 rounded" />
                                <span>Failed</span>
                            </div>
                        </div>
                    </div>
                ) : (
                    <Empty description="No daily data available" />
                )}
            </Card>

            {/* Hourly Distribution */}
            <Card
                title={
                    <Space>
                        <ClockCircleOutlined />
                        <span>Hourly Distribution (24h)</span>
                    </Space>
                }
            >
                {analytics.hourlyDistribution && analytics.hourlyDistribution.length > 0 ? (
                    <div className="flex items-end gap-1 h-32">
                        {analytics.hourlyDistribution.map((hour) => (
                            <Tooltip
                                key={hour.hour}
                                title={`${hour.hourLabel}: ${hour.totalAttempts} attempts (${hour.successfulLogins} success, ${hour.failedLogins} failed)`}
                            >
                                <div className="flex-1 flex flex-col items-center">
                                    <div
                                        className="w-full bg-blue-500 rounded-t transition-all hover:bg-blue-600"
                                        style={{
                                            height: `${maxHourlyAttempts > 0 ? (hour.totalAttempts / maxHourlyAttempts) * 100 : 0}%`,
                                            minHeight: hour.totalAttempts > 0 ? '4px' : '0'
                                        }}
                                    />
                                    <span className="text-xs text-gray-400 mt-1">
                                        {hour.hour % 6 === 0 ? hour.hourLabel : ''}
                                    </span>
                                </div>
                            </Tooltip>
                        ))}
                    </div>
                ) : (
                    <Empty description="No hourly data available" />
                )}
            </Card>

            {/* Top Users and IPs */}
            <Row gutter={[16, 16]}>
                <Col xs={24} lg={12}>
                    <Card
                        title={
                            <Space>
                                <UserOutlined />
                                <span>Most Active Users</span>
                            </Space>
                        }
                        size="small"
                    >
                        <Table
                            columns={topUsersColumns}
                            dataSource={analytics.topActiveUsers}
                            rowKey="username"
                            pagination={false}
                            size="small"
                        />
                    </Card>
                </Col>
                <Col xs={24} lg={12}>
                    <Card
                        title={
                            <Space>
                                <CloseCircleOutlined className="text-red-500" />
                                <span>Users with Most Failed Attempts</span>
                            </Space>
                        }
                        size="small"
                    >
                        {analytics.topFailedUsers && analytics.topFailedUsers.length > 0 ? (
                            <Table
                                columns={topUsersColumns}
                                dataSource={analytics.topFailedUsers}
                                rowKey="username"
                                pagination={false}
                                size="small"
                            />
                        ) : (
                            <Empty description="No failed login attempts" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        )}
                    </Card>
                </Col>
            </Row>

            {/* Top IP Addresses */}
            <Card
                title={
                    <Space>
                        <GlobalOutlined />
                        <span>Top IP Addresses</span>
                    </Space>
                }
                size="small"
            >
                <Table
                    columns={topIpsColumns}
                    dataSource={analytics.topIpAddresses}
                    rowKey="ipAddress"
                    pagination={false}
                    size="small"
                />
            </Card>
        </div>
    );
};

export default LoginAnalytics;
